import { createVocabularyBookApi, updateVocabularyBookApi } from "@/api/services/admin/learning-content";
import { CardContainer } from "@/components/commons/CardContainer";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { useVocabularyBook } from "@/features/learning-content/hooks/useVocabularyBook";
import { wordEditSchema } from "@/features/learning-content/schema/WordEditSchema";
import { TWordEditForm } from "@/features/learning-content/types/learningContent";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, CircularProgress, Stack, Typography, styled } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TWordEditPageProps = {};

// Word data settings edit page (単語帳データの設定)
export const WordEditPage: FC<TWordEditPageProps> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: industryData } = useIndustryDetail(id);
  const { data: vocabularyBookData, isLoading: isLoadingVocabularyBook } = useVocabularyBook(id);

  // Determine if this is create (no existing data) or update mode
  const isCreateMode = !vocabularyBookData && !isLoadingVocabularyBook;

  const industryName = industryData?.name ?? "";

  // Map existing API data to form values, or show empty form when no data
  const initialValues = useMemo<TWordEditForm>(() => {
    if (!vocabularyBookData) {
      return { vocabularyBookMemo: null, vocabularyBookSpreadsheetUrl: "" };
    }
    return {
      vocabularyBookMemo: vocabularyBookData.vocabularyBookMemo ?? null,
      vocabularyBookSpreadsheetUrl: vocabularyBookData.vocabularyBookSpreadsheetUrl ?? "",
    };
  }, [vocabularyBookData]);

  const methods = useForm<TWordEditForm, object, TWordEditForm>({
    defaultValues: initialValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<TWordEditForm>(wordEditSchema as any),
    mode: "all",
    reValidateMode: "onBlur",
  });

  // Reset form when API data loads
  useEffect(() => {
    if (vocabularyBookData) {
      methods.reset(initialValues);
    }
  }, [vocabularyBookData, initialValues, methods]);

  const onSubmit = async (data: TWordEditForm) => {
    if (!id) return;

    const payload = {
      vocabularyBookSpreadsheetUrl: data.vocabularyBookSpreadsheetUrl || "",
      vocabularyBookMemo: data.vocabularyBookMemo || null,
    };

    try {
      if (isCreateMode) {
        await createVocabularyBookApi(id, payload);
      } else {
        await updateVocabularyBookApi(id, payload);
      }

      toast.success(API_SUCCESS_MESSAGE);

      navigate(getDynamicRoute(AppRoutes.learningContentWordDetail, { id }));
    } catch (error) {
      showError(error);
    }
  };

  const handleBack = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.learningContentWordDetail, { id }));
    } else {
      navigate(AppRoutes.learningContent);
    }
  };

  if (isLoadingVocabularyBook) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <EditWrapper gap={4}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="center" gap={2}>
            {industryName}のコンテンツ管理 - 単語帳データの設定
          </Typography>
        }
        description={
          <Typography variant="body14">単語帳コンテンツを編集できます。学習者は単語帳としてだけでなく、単語練習としても活用できます。</Typography>
        }
        enablePageBack={false}
      />
      <Form methods={methods} onSubmit={onSubmit} schema={wordEditSchema} initialValues={initialValues}>
        <MenuNavigate
          contents={[
            {
              title: "基本情報",
              content: <BasicInfoSection />,
            },
            {
              title: "単語帳データ",
              content: <WordDataSection />,
            },
          ]}
          contentWidth="900px"
          contentAffix={
            <FooterContainer>
              <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                <Button size="sm-round" variant="secondary" onClick={handleBack}>
                  戻る
                </Button>
                <Button disabled={methods.formState.isSubmitting} size="sm-round" type="submit" startIcon={<SaveOutlinedIcon />}>
                  保存
                </Button>
              </Stack>
            </FooterContainer>
          }
        />
      </Form>
    </EditWrapper>
  );
};

// 基本情報 section - vocabulary book memo field
const BasicInfoSection: FC = () => {
  const { control } = useFormContext<TWordEditForm>();

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          基本情報
        </Typography>
        <FormField control={control} name="vocabularyBookMemo">
          <Input fullWidth placeholder="入力してください" />
        </FormField>
      </Stack>
    </CardContainer>
  );
};

// 単語帳データ section - vocabulary spreadsheet URL field
const WordDataSection: FC = () => {
  const { control } = useFormContext<TWordEditForm>();

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          単語帳データ
        </Typography>
        <FormField control={control} name="vocabularyBookSpreadsheetUrl">
          <Input fullWidth placeholder="入力してください" />
        </FormField>
      </Stack>
    </CardContainer>
  );
};

const EditWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
